/**
 * The VEXOS subscription row inside the payment schedule table.
 *
 * One row for the whole subscription — annual value, monthly fee, where it
 * stands — that expands into the month-by-month ledger. It renders <tr>s so it
 * sits in the same table as the payment milestones, but its figures are
 * DELIBERATELY not added into that table's totals or the contract-value cards
 * above it: the subscription is a separate agreement with its own ledger (see
 * backend/routers/vexos_subscriptions.py for why blending it would restate
 * exposure, the Money tab and receivables).
 *
 * Amounts only ever render for finance users — the payload arrives with every
 * dollar field nulled for PMs, so there is nothing here to leak.
 */
import { useState } from "react";
import { toast } from "sonner";
import { apiClient } from "../../App";
import {
  AlertTriangle,
  Ban,
  Calendar,
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  CreditCard,
  Loader2,
  MoreHorizontal,
  Play,
  RefreshCw,
  Repeat,
  RotateCcw,
  Trash2,
  Zap,
} from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Textarea } from "../ui/textarea";

const fmtMoney = (amount) => {
  if (amount === null || amount === undefined) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const fmtDate = (value) => {
  if (!value) return "—";
  try {
    // Date-only strings are parsed as UTC by Date(); render them in UTC too so
    // a due date of the 1st doesn't display as the last day of the prior month.
    const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(String(value));
    return new Date(value).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      ...(dateOnly ? { timeZone: "UTC" } : {}),
    });
  } catch {
    return value;
  }
};

const fmtMonthLabel = (value) => {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return null;
  }
};

const SUB_STATUS = {
  PendingDelivery: { label: "Awaiting delivery", className: "bg-slate-100 text-slate-700" },
  Active: { label: "Billing", className: "bg-green-100 text-green-700" },
  Paused: { label: "Paused", className: "bg-amber-100 text-amber-700" },
  Completed: { label: "Term complete", className: "bg-blue-100 text-blue-700" },
  Cancelled: { label: "Cancelled", className: "bg-zinc-100 text-zinc-500" },
};

// Delivered, but nobody has started billing yet. Deliberately the loudest state
// in the table: activation is manual, so this row is the only thing that will
// ever remind anyone a delivered platform isn't being charged for.
const READY_STATUS = { label: "Ready to bill", className: "bg-amber-100 text-amber-800" };

const PERIOD_STATE = {
  Scheduled: { label: "Upcoming", className: "bg-slate-100 text-slate-700" },
  Invoiced: { label: "Invoice sent", className: "bg-indigo-100 text-indigo-700" },
  Paid: { label: "Paid", className: "bg-green-100 text-green-700" },
  Failed: { label: "Card declined", className: "bg-red-100 text-red-700" },
  PastDue: { label: "Past due", className: "bg-red-100 text-red-700" },
  Waived: { label: "Waived", className: "bg-zinc-100 text-zinc-500" },
};

// PMs see where the subscription stands, never what it costs.
const PM_PERIOD_STATE = {
  Scheduled: { label: "Upcoming", className: "bg-slate-100 text-slate-700" },
  Invoiced: { label: "Billed", className: "bg-blue-100 text-blue-700" },
  Paid: { label: "Settled", className: "bg-green-100 text-green-700" },
  Failed: { label: "Needs attention", className: "bg-amber-100 text-amber-700" },
  PastDue: { label: "Needs attention", className: "bg-amber-100 text-amber-700" },
  Waived: { label: "Waived", className: "bg-zinc-100 text-zinc-500" },
};

export default function SubscriptionRow({
  programId,
  data,
  isFinance,
  colSpan,
  onChanged,
}) {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showStart, setShowStart] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [waiveTarget, setWaiveTarget] = useState(null);
  const [waiveReason, setWaiveReason] = useState("");
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [editForm, setEditForm] = useState({ monthly_amount: 0, term_months: 12, cc_fee_pct: 3 });
  const [startDate, setStartDate] = useState("");

  const sub = data?.subscription;
  const summary = data?.summary;
  const periods = data?.periods || [];
  if (!sub) return null;

  const awaitingActivation = !!summary?.awaiting_activation;
  const status = awaitingActivation
    ? READY_STATUS
    : SUB_STATUS[sub.status] || SUB_STATUS.PendingDelivery;
  const autopay = sub.stripe_subscription_id && ["active", "trialing"].includes(sub.stripe_status);
  const term = summary?.term_months || sub.term_months || 12;
  const proposedStart = summary?.proposed_billing_start_date;
  const suspension = summary?.suspension || null;

  const runSuspensionCheck = async () => {
    await call(
      () => apiClient.post(`/programs/${programId}/subscription/evaluate-suspension`),
      "Cure window checked",
    );
  };

  const liftSuspension = async () => {
    await call(
      () => apiClient.post(`/programs/${programId}/subscription/lift-suspension`),
      "Cleared in HQ — the live platform still has to be switched back on by hand",
    );
  };

  const openStartDialog = () => {
    // Default to the platform launch date, which is what the agreement and the
    // client's portal both promise — so the common case is one click, and an
    // unusual date is still one field away.
    setStartDate((proposedStart || "").slice(0, 10));
    setShowStart(true);
  };

  const call = async (fn, successMessage) => {
    setBusy(true);
    try {
      const res = await fn();
      if (successMessage) toast.success(successMessage);
      onChanged?.(res?.data);
      return res;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const openEdit = () => {
    setEditForm({
      monthly_amount: sub.monthly_amount || 0,
      term_months: term,
      cc_fee_pct: sub.cc_fee_pct ?? 3,
      billing_start_date: sub.billing_start_date || "",
    });
    setShowEdit(true);
  };

  const saveEdit = async () => {
    const ok = await call(
      () =>
        apiClient.patch(`/programs/${programId}/subscription`, {
          monthly_amount: Number(editForm.monthly_amount),
          term_months: Number(editForm.term_months),
          cc_fee_pct: Number(editForm.cc_fee_pct),
          ...(editForm.billing_start_date
            ? { billing_start_date: editForm.billing_start_date }
            : {}),
        }),
      "Subscription updated"
    );
    if (ok) setShowEdit(false);
  };

  const startBilling = async () => {
    const ok = await call(
      () =>
        apiClient.post(`/programs/${programId}/subscription/activate`, {
          billing_start_date: startDate || null,
        }),
      "Billing started"
    );
    if (ok) setShowStart(false);
  };

  const setUpCard = async () => {
    const res = await call(
      () =>
        apiClient.post(`/programs/${programId}/subscription/stripe/checkout`, {
          origin_url: window.location.origin,
        }),
      null
    );
    if (res?.data?.url) {
      setCheckoutUrl(res.data);
      onChanged?.();
    }
  };

  const monthLink = async (period) => {
    const res = await call(
      () =>
        apiClient.post(
          `/programs/${programId}/subscription/periods/${period.id}/payment-link`,
          { origin_url: window.location.origin }
        ),
      null
    );
    if (res?.data?.url) {
      setCheckoutUrl({ ...res.data, month: period.period_number });
      onChanged?.();
    }
  };

  const markPaid = (period) =>
    call(
      () =>
        apiClient.post(
          `/programs/${programId}/subscription/periods/${period.id}/mark-paid`,
          {}
        ),
      `Month ${period.period_number} recorded as paid`
    );

  const resetPeriod = (period) =>
    call(
      () =>
        apiClient.post(`/programs/${programId}/subscription/periods/${period.id}/reset`),
      `Month ${period.period_number} reset`
    );

  const submitWaive = async () => {
    if (!waiveReason.trim()) return;
    const ok = await call(
      () =>
        apiClient.post(
          `/programs/${programId}/subscription/periods/${waiveTarget.id}/waive`,
          { reason: waiveReason }
        ),
      `Month ${waiveTarget.period_number} waived`
    );
    if (ok) {
      setWaiveTarget(null);
      setWaiveReason("");
    }
  };

  const copyLink = (url) => {
    navigator.clipboard?.writeText(url);
    toast.success("Payment link copied");
  };

  return (
    <>
      {/* ── The subscription row ─────────────────────────────────────────── */}
      <tr
        className="border-b border-border bg-violet-50/40 dark:bg-violet-500/5 hover:bg-violet-50/70 dark:hover:bg-violet-500/10 transition-colors cursor-pointer"
        onClick={() => setExpanded((v) => !v)}
        data-testid="subscription-row"
      >
        <td className="p-3">
          <div className="flex items-center gap-1">
            {expanded ? (
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            )}
          </div>
        </td>
        <td className="p-3">
          <p className="font-medium text-sm flex items-center gap-1.5">
            <Repeat className="w-3.5 h-3.5 text-violet-600 dark:text-violet-400" />
            VEXOS Platform Subscription
            <span
              title="A recurring agreement — deliberately kept out of the contract value and the totals above."
              className="text-[9px] font-mono font-bold px-1 py-px rounded bg-violet-500/15 text-violet-600 dark:text-violet-400"
            >
              SUB
            </span>
          </p>
          <p className="text-xs text-muted-foreground">
            {isFinance
              ? `${term} months · ${fmtMoney(summary?.monthly_amount)}/mo · separate from contract value`
              : `${term} monthly payments · tracked separately from the contract`}
          </p>
        </td>
        <td className="p-3">
          {awaitingActivation ? (
            <>
              <p className="text-sm">{proposedStart ? fmtDate(proposedStart) : "—"}</p>
              <p className="text-xs text-muted-foreground">
                Platform launch date
                {summary?.delivered_at ? ` (${fmtDate(summary.delivered_at)})` : ""}
              </p>
              {isFinance && (
                <button
                  type="button"
                  className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                  onClick={(e) => {
                    e.stopPropagation();
                    openStartDialog();
                  }}
                  data-testid="start-billing-inline"
                >
                  <Play className="w-3 h-3" />
                  Start billing
                </button>
              )}
            </>
          ) : (
            <>
              <p className="text-sm">
                {sub.billing_start_date ? fmtDate(sub.billing_start_date) : "—"}
              </p>
              <p className="text-xs text-muted-foreground">
                {sub.status === "PendingDelivery"
                  ? "Starts once VEXOS is delivered"
                  : `Monthly · ${term}-month term`}
              </p>
            </>
          )}
        </td>
        {isFinance && (
          <>
            <td className="p-3 text-right">
              <p className="font-medium">{fmtMoney(summary?.annual_value)}</p>
              <p className="text-[11px] text-muted-foreground">
                annual{term !== 12 ? ` · ${fmtMoney(summary?.term_total)} term` : ""}
              </p>
            </td>
            <td className="p-3 text-right">
              <p
                className={
                  summary?.paid_total > 0
                    ? "text-green-600 font-medium"
                    : "text-muted-foreground"
                }
              >
                {fmtMoney(summary?.paid_total)}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {summary?.settled_count || 0} of {summary?.total_count || term} months
              </p>
            </td>
          </>
        )}
        <td className="p-3 text-center">
          <Badge className={`${status.className} text-xs`} data-testid="subscription-status">
            {status.label}
          </Badge>
        </td>
        {isFinance && (
          <td className="p-3 text-center">
            {autopay ? (
              <span
                className="inline-flex items-center gap-1 text-xs text-green-600"
                title="Card on file — Stripe charges this subscription automatically each month."
              >
                <Zap className="w-3 h-3" />
                Autopay
              </span>
            ) : sub.stripe_status === "awaiting_card" && sub.stripe_checkout_url ? (
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                onClick={(e) => {
                  e.stopPropagation();
                  copyLink(sub.stripe_checkout_url);
                }}
              >
                <Copy className="w-3 h-3" />
                Copy card link
              </button>
            ) : sub.stripe_status === "past_due" ? (
              <span className="text-xs text-red-600 whitespace-nowrap">Card failing</span>
            ) : (
              <span className="text-xs text-muted-foreground whitespace-nowrap">No card</span>
            )}
          </td>
        )}
        <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
          {isFinance ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  data-testid="subscription-menu"
                >
                  {busy ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <MoreHorizontal className="w-4 h-4" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {sub.status === "PendingDelivery" && (
                  <DropdownMenuItem onClick={openStartDialog}>
                    <Play className="w-4 h-4 mr-2" />
                    Start billing
                  </DropdownMenuItem>
                )}
                {!autopay && ["Active", "PendingDelivery", "Paused"].includes(sub.status) && (
                  <DropdownMenuItem onClick={setUpCard} disabled={!data?.stripe_configured}>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Set up card on file (Stripe)
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={openEdit}>
                  <Calendar className="w-4 h-4 mr-2" />
                  Edit terms
                </DropdownMenuItem>
                {sub.stripe_checkout_session_id && (
                  <DropdownMenuItem
                    onClick={() =>
                      call(
                        () =>
                          apiClient.post(
                            `/programs/${programId}/subscription/stripe/sync`
                          ),
                        "Synced from Stripe"
                      )
                    }
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Sync from Stripe
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                {sub.status !== "Cancelled" && (
                  <DropdownMenuItem
                    className="text-red-600"
                    onClick={() => setShowCancel(true)}
                  >
                    <Ban className="w-4 h-4 mr-2" />
                    Cancel subscription
                  </DropdownMenuItem>
                )}
                {!summary?.paid_total && !sub.stripe_subscription_id && (
                  <DropdownMenuItem
                    className="text-red-600"
                    onClick={() => setShowDelete(true)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remove subscription
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </td>
      </tr>

      {/* ── Failed payment, the cure clock, and the suspension ──────────────
          The one row on this panel that describes a consequence rather than a
          figure, so it sits above the months and reads in a sentence: what
          failed, how long the client has, and — once it has run out — that HQ
          has recorded a suspension it cannot itself enforce. */}
      {suspension && (
        <tr className={`border-b border-border ${
          suspension.suspended ? "bg-red-50/70 dark:bg-red-500/5" : "bg-amber-50/60 dark:bg-amber-500/5"
        }`}>
          <td className="p-3" />
          <td colSpan={colSpan - 1} className="px-3 py-2.5">
            <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
              <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${
                suspension.suspended ? "text-red-600" : "text-amber-600"
              }`} />
              <div className="min-w-0 flex-1">
                {suspension.suspended ? (
                  <>
                    <p className="text-sm font-medium text-red-800 dark:text-red-300">
                      Platform suspended for non-payment
                      {suspension.period_number ? ` — month ${suspension.period_number}` : ""}
                    </p>
                    <p className="text-xs text-red-700/90 dark:text-red-300/80 mt-0.5">
                      Recorded {fmtDate(suspension.suspended_at)}. The client has been emailed.
                      {" "}
                      <strong>HQ can't switch the live platform off</strong> — the VEXOS manager
                      still has to take it down, and bring it back when this is settled.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                      Card declined
                      {suspension.period_number ? ` on month ${suspension.period_number}` : ""} —
                      {" "}
                      {suspension.days_left > 0
                        ? `${suspension.days_left} day${suspension.days_left === 1 ? "" : "s"} left to fix it`
                        : "the cure window has run out"}
                    </p>
                    <p className="text-xs text-amber-800/90 dark:text-amber-200/80 mt-0.5">
                      Failed {fmtDate(suspension.first_failed_at)}. The client was
                      {suspension.client_notified_at ? " emailed" : " NOT emailed — no client address on the tracker"}.
                      {" "}Platform pauses after {suspension.grace_days} days unpaid.
                    </p>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {suspension.invoice_url && (
                  <a href={suspension.invoice_url} target="_blank" rel="noreferrer"
                    className="text-xs text-primary hover:underline">
                    Stripe invoice
                  </a>
                )}
                {isFinance && suspension.overdue_for_suspension && (
                  <Button size="sm" variant="outline" className="h-7 text-xs rounded-full"
                    disabled={busy} onClick={runSuspensionCheck}>
                    Suspend now
                  </Button>
                )}
                {isFinance && (suspension.suspended || suspension.state === "in_cure_window") && (
                  <Button size="sm" variant="ghost" className="h-7 text-xs rounded-full"
                    disabled={busy} onClick={liftSuspension}>
                    {suspension.suspended ? "Lift suspension" : "Clear the flag"}
                  </Button>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}

      {/* ── The subscription and its quote disagree ─────────────────────────
          Reps renegotiate. While a subscription is dormant and untouched HQ
          just follows the quote silently; once it's started, billed something,
          or had its terms typed in here by hand, following the quote would
          throw away a decision someone already made, so the difference is shown
          and finance decides. `reason` says which of the two it is. */}
      {isFinance && data?.quote_mismatch && (
        <tr className="border-b border-border bg-amber-50/60 dark:bg-amber-500/5">
          <td className="p-3" />
          <td colSpan={colSpan - 1} className="px-3 py-2.5">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
              <p className="text-xs">
                The linked quote now says{" "}
                <strong>{fmtMoney(data.quote_mismatch.to?.monthly_amount)}/mo</strong>
                {data.quote_mismatch.to?.term_months !== data.quote_mismatch.from?.term_months
                  ? ` × ${data.quote_mismatch.to?.term_months} months`
                  : ""}{" "}
                — this subscription is set to{" "}
                <strong>{fmtMoney(data.quote_mismatch.from?.monthly_amount)}/mo</strong>.{" "}
                {data.quote_mismatch.reason === "manual"
                  ? "It won't change on its own because that figure was entered here by hand."
                  : "It won't change on its own because the term has already started."}
              </p>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                disabled={busy}
                onClick={() =>
                  call(
                    () =>
                      apiClient.post(
                        `/programs/${programId}/subscription/sync-from-quote`
                      ),
                    "Quote terms applied"
                  )
                }
                data-testid="apply-quote-terms"
              >
                {busy ? (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <RefreshCw className="w-3 h-3 mr-1" />
                )}
                Use the quote's figure
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">
              Months already paid or waived keep the amount they were settled at.
            </p>
          </td>
        </tr>
      )}

      {/* ── The months ───────────────────────────────────────────────────── */}
      {expanded && periods.length === 0 && (
        <tr className="border-b border-border bg-muted/10">
          <td colSpan={colSpan} className="px-3 py-4 text-center text-xs text-muted-foreground">
            No months scheduled yet.
          </td>
        </tr>
      )}
      {expanded &&
        periods.map((p) => {
          const state = p.display_state || p.status;
          const tone = (isFinance ? PERIOD_STATE : PM_PERIOD_STATE)[state] ||
            PERIOD_STATE.Scheduled;
          const monthLabel = fmtMonthLabel(p.period_start);
          return (
            <tr
              key={p.id}
              className="border-b border-border/60 bg-muted/10 text-sm"
              data-testid={`subscription-period-${p.period_number}`}
            >
              <td className="p-2" />
              <td className="py-2 pl-9 pr-3">
                <p className="text-sm">
                  Month {p.period_number}
                  {monthLabel ? (
                    <span className="text-muted-foreground"> · {monthLabel}</span>
                  ) : null}
                </p>
                {p.period_start && p.period_end && (
                  <p className="text-[11px] text-muted-foreground">
                    {fmtDate(p.period_start)} – {fmtDate(p.period_end)}
                  </p>
                )}
              </td>
              <td className="py-2 px-3">
                <p className="text-sm">{p.due_date ? fmtDate(p.due_date) : "—"}</p>
                {!p.due_date && (
                  <p className="text-[11px] text-muted-foreground">
                    Dated when billing starts
                  </p>
                )}
              </td>
              {isFinance && (
                <>
                  <td className="py-2 px-3 text-right">{fmtMoney(p.amount)}</td>
                  <td className="py-2 px-3 text-right">
                    <span
                      className={
                        p.paid_amount > 0 ? "text-green-600" : "text-muted-foreground"
                      }
                    >
                      {p.paid_amount > 0 ? fmtMoney(p.paid_amount) : "—"}
                    </span>
                  </td>
                </>
              )}
              <td className="py-2 px-3 text-center">
                <Badge className={`${tone.className} text-xs`}>{tone.label}</Badge>
              </td>
              {isFinance && (
                <td className="py-2 px-3 text-center">
                  {p.hosted_invoice_url || p.stripe_payment_link ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                      onClick={() => copyLink(p.hosted_invoice_url || p.stripe_payment_link)}
                    >
                      <Copy className="w-3 h-3" />
                      Link
                    </button>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
              )}
              <td className="py-2 px-3 text-right">
                {isFinance && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7">
                        <MoreHorizontal className="w-3.5 h-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {!["Paid", "Waived"].includes(p.status) && (
                        <>
                          <DropdownMenuItem onClick={() => markPaid(p)}>
                            <Check className="w-4 h-4 mr-2" />
                            Mark paid
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => monthLink(p)}
                            disabled={!data?.stripe_configured}
                          >
                            <CreditCard className="w-4 h-4 mr-2" />
                            Send this month's link
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setWaiveTarget(p);
                              setWaiveReason("");
                            }}
                          >
                            <Ban className="w-4 h-4 mr-2" />
                            Waive this month
                          </DropdownMenuItem>
                        </>
                      )}
                      {["Paid", "Waived", "Invoiced", "Failed"].includes(p.status) &&
                        !p.stripe_invoice_id && (
                          <DropdownMenuItem onClick={() => resetPeriod(p)}>
                            <RotateCcw className="w-4 h-4 mr-2" />
                            Reset to upcoming
                          </DropdownMenuItem>
                        )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </td>
            </tr>
          );
        })}

      {/* ── Dialogs ──────────────────────────────────────────────────────── */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Subscription terms</DialogTitle>
            <DialogDescription>
              Changing the monthly fee or the term re-dates every month that hasn't been
              paid or waived yet. Months already settled are left alone. What you enter
              here sticks: it takes this subscription off the linked quote, so a later
              quote edit is flagged for you instead of overwriting it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Monthly amount (USD)</Label>
              <Input
                type="number"
                min="0"
                value={editForm.monthly_amount}
                onChange={(e) =>
                  setEditForm({ ...editForm, monthly_amount: e.target.value })
                }
              />
            </div>
            <div>
              <Label>Term (months)</Label>
              <Input
                type="number"
                min="1"
                max="120"
                value={editForm.term_months}
                onChange={(e) => setEditForm({ ...editForm, term_months: e.target.value })}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Annual value ={" "}
                {fmtMoney((Number(editForm.monthly_amount) || 0) * 12)} · full term ={" "}
                {fmtMoney(
                  (Number(editForm.monthly_amount) || 0) *
                    (Number(editForm.term_months) || 0)
                )}
              </p>
            </div>
            <div>
              <Label>Card processing fee (%)</Label>
              <Input
                type="number"
                min="0"
                max="20"
                step="0.5"
                value={editForm.cc_fee_pct}
                onChange={(e) => setEditForm({ ...editForm, cc_fee_pct: e.target.value })}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Added as its own line on the Stripe charge, exactly like our one-time
                payments. Set it to 0 for a client paying by ACH or cheque.
              </p>
            </div>
            {sub.status !== "PendingDelivery" && (
              <div>
                <Label>Billing start date</Label>
                <Input
                  type="date"
                  value={(editForm.billing_start_date || "").slice(0, 10)}
                  onChange={(e) =>
                    setEditForm({ ...editForm, billing_start_date: e.target.value })
                  }
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(false)}>
              Cancel
            </Button>
            <Button onClick={saveEdit} disabled={busy}>
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showStart} onOpenChange={setShowStart}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start billing</DialogTitle>
            <DialogDescription>
              {awaitingActivation
                ? `This platform was delivered${
                    summary?.delivered_at ? ` on ${fmtDate(summary.delivered_at)}` : ""
                  }, so the first charge is set to land on that launch date, per the
                  agreement. Nothing has been billed yet — this is what starts the term.`
                : `This starts the client's billing term. Delivery doesn't do it on its own,
                   so this is also how a platform that went live before HQ tracked it gets
                   onto the books.`}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>First month starts on</Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {term} months will be dated from here.
              {awaitingActivation && proposedStart
                ? ` Pre-filled with ${fmtDate(proposedStart)} — change it if you're billing
                   from a different date.`
                : " Leave blank to start today."}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStart(false)}>
              Cancel
            </Button>
            <Button onClick={startBilling} disabled={busy}>
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Start billing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!waiveTarget} onOpenChange={(open) => !open && setWaiveTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Waive month {waiveTarget?.period_number}</DialogTitle>
            <DialogDescription>
              The month stays on the ledger, marked as not charged. The client sees "Not
              charged" — this reason is internal.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Reason</Label>
            <Textarea
              value={waiveReason}
              onChange={(e) => setWaiveReason(e.target.value)}
              placeholder="Why isn't this month being charged?"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWaiveTarget(null)}>
              Cancel
            </Button>
            <Button onClick={submitWaive} disabled={busy || !waiveReason.trim()}>
              Waive month
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCancel} onOpenChange={setShowCancel}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel this subscription?</DialogTitle>
            <DialogDescription>
              {autopay
                ? "Stripe stops charging at the end of the month the client has already paid for. Nothing is refunded."
                : "The subscription is marked cancelled in HQ. No future months will be billed."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancel(false)}>
              Keep it
            </Button>
            <Button
              variant="destructive"
              disabled={busy}
              onClick={async () => {
                const ok = await call(
                  () =>
                    apiClient.post(`/programs/${programId}/subscription/stripe/cancel`),
                  "Subscription cancelled"
                );
                if (ok) setShowCancel(false);
              }}
            >
              Cancel subscription
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove this subscription?</DialogTitle>
            <DialogDescription>
              Deletes the row and its months. Only possible because no month has been paid
              — use Cancel instead once money has moved.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDelete(false)}>
              Keep it
            </Button>
            <Button
              variant="destructive"
              disabled={busy}
              onClick={async () => {
                const ok = await call(
                  () => apiClient.delete(`/programs/${programId}/subscription`),
                  "Subscription removed"
                );
                if (ok) {
                  setShowDelete(false);
                  onChanged?.();
                }
              }}
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!checkoutUrl} onOpenChange={(open) => !open && setCheckoutUrl(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {checkoutUrl?.month
                ? `Payment link for month ${checkoutUrl.month}`
                : "Card-on-file link"}
            </DialogTitle>
            <DialogDescription>
              {checkoutUrl?.month
                ? "Send this to the client to collect this single month."
                : `Send this to the client. They enter their card once and Stripe charges ${fmtMoney(
                    checkoutUrl?.monthly_amount
                  )}/mo automatically${
                    checkoutUrl?.first_charge_date &&
                    checkoutUrl.first_charge_date !== "immediately"
                      ? `, first charge ${fmtDate(checkoutUrl.first_charge_date)}`
                      : ", starting immediately"
                  }.`}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Input readOnly value={checkoutUrl?.url || ""} className="text-xs" />
            <Button variant="outline" onClick={() => copyLink(checkoutUrl.url)}>
              <Copy className="w-4 h-4" />
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setCheckoutUrl(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * The "no subscription yet" affordance — a single table row offering to add one,
 * pre-filled from the linked VEXOS quote when there is one.
 */
export function AddSubscriptionRow({ programId, suggested, colSpan, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    monthly_amount: suggested?.monthly_amount || "",
    term_months: suggested?.term_months || 12,
  });

  const submit = async () => {
    if (!Number(form.monthly_amount)) {
      toast.error("Enter the monthly amount");
      return;
    }
    setBusy(true);
    try {
      const res = await apiClient.post(`/programs/${programId}/subscription`, {
        monthly_amount: Number(form.monthly_amount),
        term_months: Number(form.term_months) || 12,
      });
      toast.success("Subscription added");
      setOpen(false);
      onChanged?.(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add subscription");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <tr className="border-b border-border">
        <td colSpan={colSpan} className="p-3">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 text-xs text-violet-600 dark:text-violet-400 hover:underline"
            onClick={() => setOpen(true)}
            data-testid="add-subscription-btn"
          >
            <Repeat className="w-3.5 h-3.5" />
            Add VEXOS subscription
            {suggested ? (
              <span className="text-muted-foreground">
                — quote has {fmtMoney(suggested.monthly_amount)}/mo ×{" "}
                {suggested.term_months}
              </span>
            ) : null}
          </button>
        </td>
      </tr>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add VEXOS subscription</DialogTitle>
            <DialogDescription>
              Tracked separately from the contract value. It stays dormant until someone
              starts billing — delivering the platform readies it and proposes a first
              charge on the launch date, but never starts the term on its own.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Monthly amount (USD)</Label>
              <Input
                type="number"
                min="0"
                value={form.monthly_amount}
                onChange={(e) => setForm({ ...form, monthly_amount: e.target.value })}
              />
            </div>
            <div>
              <Label>Term (months)</Label>
              <Input
                type="number"
                min="1"
                max="120"
                value={form.term_months}
                onChange={(e) => setForm({ ...form, term_months: e.target.value })}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Annual value = {fmtMoney((Number(form.monthly_amount) || 0) * 12)}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={busy}>
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Add subscription
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
